import { Loader } from "@logora/debate.progress.loader";
import { InterventionBox } from "@logora/parliament.debate.intervention_box";
import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useDataProvider } from "react-admin";
import { useNavigate } from "react-router-dom";
import { sanitize } from "../formatters";
import styles from "./DebatShow.module.scss";

const CODE_STYLES = {
	TITLE: ["Titre", "Présidence"],
	SUBTITLE: ["Sous-tit_info", "Sous-tit_p_cap", "Sous-titre bdc"],
};

export const DebatDetails = ({ resource }) => {
	const [isLoading, setIsLoading] = useState(true);
	const [interventions, setInterventions] = useState([]);
	const dataProvider = useDataProvider();
	const navigate = useNavigate();

	useEffect(() => {
		if (resource) {
			fetchInterventions();
		}
	}, [resource]);

	const fetchInterventions = useCallback(async () => {
		try {
			const response = await dataProvider.getList("interventions", {
				filter: { debatRefUid: resource.uid },
				include: ["acteurRef.groupeParlementaire", "acteurRef.mandatPrincipal"],
				sort: { field: "ordreAbsoluSeance", order: "asc" },
				pagination: { perPage: 1000 },
			});
			const interventions = response.data;
			if (interventions) {
				setInterventions(interventions);
			}
			setIsLoading(false);
		} catch (error) {
			console.log(error);
			setIsLoading(false);
		}
	}, [dataProvider]);

	const displayIntervention = (intervention) => {
		if (!intervention.texte) return null;

		const isTitle = CODE_STYLES.TITLE.includes(intervention.codeStyle);
		const isSubtitle = CODE_STYLES.SUBTITLE.includes(intervention.codeStyle);

		if (isTitle || isSubtitle) {
			return (
				<div
					id={
						isTitle
							? intervention.dossierRefUid || intervention.id
							: intervention.id
					}
					className={
						isTitle ? styles.interventionTitle : styles.interventionSubtitle
					}
					dangerouslySetInnerHTML={sanitize(intervention.texte)}
				/>
			);
		}
		return (
			<InterventionBox
				key={intervention.ordreAbsoluSeance}
				intervention={intervention}
				memberProps={{
					onClick: () => handleClick(intervention.acteurRefUid),
				}}
			/>
		);
	};

	const summary = useMemo(
		() =>
			interventions.reduce((summary, intervention) => {
				if (intervention.codeStyle === "Titre") {
					summary.push({
						id: intervention.dossierRefUid || intervention.id,
						title: intervention.texte,
						subtitles: [],
					});
				} else if (CODE_STYLES.SUBTITLE.includes(intervention.codeStyle)) {
					if (summary.length) {
						summary[summary.length - 1].subtitles.push({
							id: intervention.id,
							title: intervention.texte,
						});
					} else {
						summary.push({
							id: intervention.dossierRefUid || intervention.id,
							subtitles: [
								{
									id: intervention.id,
									title: intervention.texte,
								},
							],
						});
					}
				}

				return summary;
			}, []),
		[interventions, CODE_STYLES],
	);

	const handleClick = (acteurId) => {
		window
			.open(new URL(`/#/parliament/acteurs/${acteurId}`, location.origin))
			.focus();
	};

	return (
		<>
			{interventions?.length > 0 ? (
				<div className={styles.container}>
					<DebatSummary
						reunionRefUid={resource.reunionRefUid}
						summary={summary}
					/>
					<div className={styles.corps}>
						{interventions.map(displayIntervention)}
					</div>
				</div>
			) : (
				<>
					{isLoading ? (
						<div className={styles.loader}>
							<Loader />
						</div>
					) : (
						<div>Aucune intervention dans le débat</div>
					)}
				</>
			)}
		</>
	);
};

export const DebatSummary = ({ reunionRefUid, summary }) => {
	const navigate = useNavigate();

	const handleClick = (id) => {
		navigate(`/parliament/reunions/${reunionRefUid}#${id}`, {
			replace: true,
		});

		document.getElementById(id).scrollIntoView({
			behavior: "smooth",
		});
	};

	return (
		<ol className={styles.summary}>
			{summary.map(({ id, title, subtitles }) => (
				<li key={id}>
					{title && (
						<button
							type="button"
							onClick={() => handleClick(id)}
							className={styles.summaryTitle}
							dangerouslySetInnerHTML={sanitize(title)}
						/>
					)}
					<ol>
						{subtitles.map((subtitle) => (
							<li key={subtitle}>
								<button
									type="button"
									onClick={() => handleClick(subtitle.id)}
									className={styles.summarySubtitle}
									dangerouslySetInnerHTML={sanitize(subtitle.title)}
								/>
							</li>
						))}
					</ol>
				</li>
			))}
		</ol>
	);
};
